import React from "react";
import {
  AGGRESSION_PROMPT,
  TEXT_TO_NO_SQL_PROMPT,
} from "../../../../../../utils/defaultPrompts";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tab,
  Tabs,
  TextField,
  Typography,
} from "@mui/material";
import colors from "../../../../../../Constants/colors";
import { Clear } from "@mui/icons-material";
import { useState, useMemo } from "react";
import { btnStyles } from "./UploadDatasetsNodeStyles";
import { dialogStyles } from "../../../../../../Constants/dialogStyles";
import { primaryButtonStyles } from "../../../../../../Constants/buttonStyles";

const TextToNoSQLDialog = ({ isPromptDialogOpen, closePromptDialog, data }) => {
  const extractAggressionPromptParts = (prompt = "") => {
    const cleaned = prompt.replace(/^"""/, "").replace(/"""$/, "").trim();

    const userQuestionMarker = "*User Question:*";
    const splitIndex = cleaned.indexOf(userQuestionMarker);

    const header = cleaned.slice(0, splitIndex).trim();
    const footer = cleaned.slice(splitIndex).trim();

    return {
      header,
      footer,
    };
  };

  const extractAdditionalInfo = (fullPrompt) => {
    // Adjust the marker based on what you are using in your prompt
    const additionalMarker = "Additional Instructions:";
    // The regex:
    // - Matches the marker, then any whitespace/newline,
    // - Lazily captures text until it sees a newline followed by "*User Question:" (or the end of string)
    const regex = new RegExp(
      `${additionalMarker}\\s*\\n([\\s\\S]*?)(?=\\n\\*User Question:|$)`,
      "i"
    );
    const match = regex.exec(fullPrompt);

    if (match) {
      let extractedText = match[1].trim();

      // Remove any trailing "" if mistakenly included
      if (extractedText.endsWith(`""`)) {
        extractedText = extractedText.slice(0, -2).trim();
      }
      return extractedText;
    }

    return "";
  };

  const rawAggressionPrompt =
    localStorage.getItem(`aggregation_prompt_${data.nodeId}`) ||
    AGGRESSION_PROMPT;

  const { header, footer } = extractAggressionPromptParts(rawAggressionPrompt);

  const [aggressionPromptHeader] = useState(header); // non-editable
  const [aggressionPromptFooter] = useState(footer); // also non-editable
  const initialAggressionAdditional =
    extractAdditionalInfo(rawAggressionPrompt);
  const [aggressionAdditional, setAggressionAdditional] = useState(
    initialAggressionAdditional
  );

  const extractPromptSections = (fullPrompt = "") => {
    const cleaned = fullPrompt.replace(/^""/, "").replace(/""$/, "").trim();

    const fixedStart = "Note : Only answer in yes or no";
    const additionalStart = "Additional Instructions:";

    // Get position of the fixed section
    const fixedIndex = cleaned.indexOf(fixedStart);
    const additionalIndex = cleaned.indexOf(additionalStart);

    // Slice out editable section
    const editable =
      fixedIndex !== -1 ? cleaned.slice(0, fixedIndex).trim() : cleaned;

    // Slice out additional section if present
    const additional =
      additionalIndex !== -1
        ? cleaned.slice(additionalIndex + additionalStart.length).trim()
        : "";

    return { editable, additional };
  };

  const storedPrompt =
    JSON.parse(localStorage.getItem(`text2nosql_prompt_${data.nodeId}`))
      ?.TEXT_TO_NO_SQL_PROMPT || TEXT_TO_NO_SQL_PROMPT;

  const { editable, additional } = extractPromptSections(storedPrompt);

  const [editableText2NoSQLPrompt, setEditableText2NoSQLPrompt] =
    useState(editable);
  const [text2NoSQLAdditional, setText2NoSQLAdditional] = useState(additional);
  // loader for saving nosql prompts
  const [savePromptLoading, setSavePromptLoading] = useState(false);

  const [aggressionSaveLoading, setAggressionSaveLoading] = useState(false);

  const fixedText2NoSQL = `
    Note : Only answer in yes or no

    query : {query}

    Your Answer : yes/no`;

  const buildFinalPrompt = (editablePrompt, additionalText) => {
    let prompt = editablePrompt.trim() + fixedText2NoSQL;

    if (additionalText.trim()) {
      prompt += `\n\nAdditional Instructions:\n${additionalText.trim()}`;
    }

    return `""\n${prompt}\n""`;
  };

  const buildFullAggressionPrompt = (additional) => {
    const full = `"""\n${aggressionPromptHeader}\n\n${
      additional.trim()
        ? `Additional Instructions:\n${additional.trim()}\n\n`
        : ""
    }${aggressionPromptFooter}\n"""`;
    return full;
  };

  // to disable save button after saving
  const [lastSavedPromptT2NoSQL, setLastSavedPromptT2NoSQL] = useState(() =>
    buildFinalPrompt(editableText2NoSQLPrompt, text2NoSQLAdditional)
  );
  const [aggressionLastSavedPrompt, setAggressionLastSavedPrompt] = useState(
    () => buildFullAggressionPrompt(aggressionAdditional)
  );
  const isText2NoSqlPromptModified = useMemo(() => {
    const currentPrompt = buildFinalPrompt(
      editableText2NoSQLPrompt,
      text2NoSQLAdditional
    );
    return currentPrompt !== lastSavedPromptT2NoSQL;
  }, [editableText2NoSQLPrompt, text2NoSQLAdditional, lastSavedPromptT2NoSQL]);

  const isAggressionPromptModified = useMemo(() => {
    return (
      buildFullAggressionPrompt(aggressionAdditional) !==
      aggressionLastSavedPrompt
    );
  }, [aggressionAdditional, aggressionLastSavedPrompt]);

  const [selectedTab, setSelectedTab] = useState(0);

  // Save updated Text-To-NoSQL prompt
  const saveText2NoSQLPrompt = () => {
    setSavePromptLoading(true);
    data.showSnackbar("Saving prompt...");

    try {
      const fullPrompt = buildFinalPrompt(
        editableText2NoSQLPrompt,
        text2NoSQLAdditional
      );

      data.setBotData((prevData) => ({
        ...prevData,
        text2nosql_prompt: { TEXT_TO_NO_SQL_PROMPT: fullPrompt },
      }));

      localStorage.setItem(
        `text2nosql_prompt_${data.nodeId}`,
        JSON.stringify({ TEXT_TO_NO_SQL_PROMPT: fullPrompt })
      );

      setLastSavedPromptT2NoSQL(fullPrompt); // ✅ update saved version

      data.showSnackbar("Text-to-NoSQL prompt saved successfully.", "success");
    } catch (err) {
      console.error("Error saving prompt:", err);
      data.showSnackbar("Failed to save prompt. Please try again.", "error");
    } finally {
      setSavePromptLoading(false);
    }
  };

  const saveAggressionPrompt = () => {
    setAggressionSaveLoading(true);
    data.showSnackbar("Saving aggregation prompt...");

    try {
      const fullPrompt = buildFullAggressionPrompt(aggressionAdditional);

      data.setBotData((prevData) => ({
        ...prevData,
        aggregation_prompt: fullPrompt,
      }));

      localStorage.setItem(`aggregation_prompt_${data.nodeId}`, fullPrompt);
      setAggressionLastSavedPrompt(fullPrompt);

      data.showSnackbar("Aggregation prompt saved successfully.", "success");
    } catch (err) {
      data.showSnackbar("Failed to save aggregation prompt.", "error");
    } finally {
      setAggressionSaveLoading(false);
    }
  };
  return (
    <Dialog
      open={isPromptDialogOpen}
      onClose={closePromptDialog}
      fullWidth
      maxWidth="md"
      PaperProps={{ sx: dialogStyles.dialogRoot }}
    >
      <DialogTitle sx={dialogStyles.dialogTitle}>
        <Clear onClick={closePromptDialog} sx={dialogStyles.dialogCloseIcon} />
        <Box sx={{ flexGrow: 1, textAlign: "center" }}>
          <Tabs
            value={selectedTab}
            onChange={(e, newValue) => setSelectedTab(newValue)}
            centered
            TabIndicatorProps={{
              sx: { backgroundColor: colors.primaryBlue },
            }}
            sx={{
              "& .MuiTab-root": {
                color: colors.extremeWhite,
                textTransform: "none",
                fontSize: "13px",
              },
              "& .Mui-selected": {
                fontWeight: "bold",
                color: colors.extremeWhite,
                fontSize: "13px",
              },
            }}
          >
            <Tab label="Text-to-NoSQL Query Conditions" />
            <Tab label="Query Creation instructions" />
          </Tabs>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ ...dialogStyles.dialogContent, mt: 2 }}>
        <Box
          sx={{
            ...dialogStyles.paperSection,
            maxHeight: "none",
            overflowY: "visible",
            height: "auto",
          }}
        >
          {selectedTab === 0 && (
            <>
              <Typography variant="caption">
                Define what type of queries should invoke a
                text-to-NoSQL pipeline
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  bgcolor: colors.extremeBlack,
                  padding: 2,
                  borderRadius: "8px",
                  mt: 2,
                }}
              >
                {/* Editable intro prompt */}
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  value={editableText2NoSQLPrompt}
                  onChange={(e) => setEditableText2NoSQLPrompt(e.target.value)}
                  placeholder="Define when to use the Text-To-NoSQL pipeline..."
                  sx={{
                    backgroundColor: colors.extremeBlack,
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    color: colors.extremeWhite,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                      "&:hover fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "10px",
                      color: colors.extremeWhite,
                      fontSize: "13px",
                      fontFamily: "monospace",
                    },
                  }}
                />

                {/* Fixed instructions (non-editable) */}
                <Box
                  sx={{
                    padding: 2,
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    overflowX: "auto",
                    color: colors.extremeWhite,
                    fontSize: "13px",
                    backgroundColor: colors.darkGrey,
                    borderRadius: "8px",
                    border: `1px solid ${colors.extremeBlack}`,
                  }}
                >
                  Note : Only answer in yes or no
                  {"\n\n"}query : {"{query}"}
                  {"\n\n"}Your Answer : yes/no
                </Box>

                {/* Additional user instructions */}
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  value={text2NoSQLAdditional}
                  onChange={(e) => setText2NoSQLAdditional(e.target.value)}
                  placeholder="Enter additional instructions here (optional)..."
                  sx={{
                    backgroundColor: colors.extremeBlack,
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    color: colors.extremeWhite,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                      "&:hover fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "10px",
                      color: colors.extremeWhite,
                      fontSize: "13px",
                      fontFamily: "monospace",
                    },
                  }}
                />
              </Box>
            </>
          )}

          {selectedTab === 1 && (
            <>
              <Typography variant="caption">
                Enter domain specific instructions that the LLM should take into
                account while creating a text-2-NoSQL query
              </Typography>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 2,
                  bgcolor: colors.extremeBlack,
                  padding: 2,
                  borderRadius: "8px",
                  mt: 2,
                }}
              >
                {/* Editable "Additional Instructions" section */}
                <TextField
                  fullWidth
                  multiline
                  rows={5}
                  value={aggressionAdditional}
                  onChange={(e) => setAggressionAdditional(e.target.value)}
                  placeholder="Enter additional instructions here..."
                  sx={{
                    backgroundColor: colors.extremeBlack,
                    borderRadius: "8px",
                    fontFamily: "monospace",
                    color: colors.extremeWhite,
                    "& .MuiOutlinedInput-root": {
                      "& fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                      "&:hover fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                      "&.Mui-focused fieldset": {
                        border: `1px solid ${colors.extremeBlack}`,
                      },
                    },
                    "& .MuiInputBase-input": {
                      padding: "10px",
                      color: colors.extremeWhite,
                      fontSize: "13px",
                      fontFamily: "monospace",
                    },
                  }}
                />
              </Box>
            </>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={dialogStyles.dialogActions}>
        <Button
          onClick={
            selectedTab === 0 ? saveText2NoSQLPrompt : saveAggressionPrompt
          }
          sx={primaryButtonStyles}
          disabled={
            selectedTab === 0
              ? savePromptLoading || !isText2NoSqlPromptModified
              : aggressionSaveLoading || !isAggressionPromptModified
          }
        >
          {(selectedTab === 0 ? savePromptLoading : aggressionSaveLoading)
            ? "Saving..."
            : `Save ${
                selectedTab === 0
                  ? "Text-to-NoSQL Query Conditions"
                  : "Query Creation Instructions"
              }`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TextToNoSQLDialog;
